import type { Event } from '@mr-website/schemas';

export const EVENT_AT_CAPACITY_LABEL = 'Event is at Capacity';

export function isEventAtCapacity(
  event: Pick<Event, 'is_online' | 'is_full' | 'max_capacity' | 'rsvp_count'>,
): boolean {
  if (event.is_online) return false;
  if (event.is_full != null) return event.is_full;
  const maxCapacity = event.max_capacity ?? 0;
  return maxCapacity > 0 && event.rsvp_count >= maxCapacity;
}

/**
 * Max spouse + guests the member may include in this RSVP.
 *
 * `null` means no extra website cap (online events, or CRM did not send remaining spots).
 * Managing an existing RSVP can keep the current party even when the event is full,
 * but cannot grow it past remaining seats.
 */
export function getMaxCompanions(
  event: Pick<Event, 'is_online' | 'spots_remaining' | 'user_rsvp'>,
  isManaging: boolean,
): number | null {
  if (event.is_online || event.spots_remaining == null) return null;
  if (!isManaging) return Math.max(0, event.spots_remaining - 1);

  const existing = (event.user_rsvp?.has_spouse ? 1 : 0) + (event.user_rsvp?.guest_count ?? 0);
  return existing + event.spots_remaining;
}

export function getMaxGuests(maxCompanions: number | null, spouseEnabled: boolean): number {
  if (maxCompanions == null) return 4;
  return Math.min(4, Math.max(0, maxCompanions - (spouseEnabled ? 1 : 0)));
}

export function canEnableSpouse(
  maxCompanions: number | null,
  spouseEnabled: boolean,
  guestCount: number,
): boolean {
  if (maxCompanions == null || spouseEnabled) return true;
  return guestCount < maxCompanions;
}

export function companionCount(spouseEnabled: boolean, guestCount: number): number {
  return (spouseEnabled ? 1 : 0) + guestCount;
}

export function isOverCompanionLimit(
  maxCompanions: number | null,
  spouseEnabled: boolean,
  guestCount: number,
): boolean {
  if (maxCompanions == null) return false;
  return companionCount(spouseEnabled, guestCount) > maxCompanions;
}
